import type { Dayjs } from 'dayjs';

import type { OpeningWindow } from '@/api/types';
import { nowInZone } from './time';

/**
 * When the weekly schedule next changes its mind.
 *
 * `GET /pharmacies/:id/open-now` answers whether a shop is open, which is the
 * question the server has to answer for the marketplace. It does not answer the
 * question the person standing at the counter has, which is **when does this
 * change** — and without it "Outside today's hours" is a fact nobody can act on.
 * They have to open the hours page and read the grid to find out whether they
 * are ten minutes early or closed until Monday.
 *
 * Derived here rather than added to the endpoint because it is presentation: the
 * schedule is already fetchable, the arithmetic is small, and a second server
 * field that could disagree with `isOpen` is worse than no field.
 *
 * On the **platform** clock, always. The server compares a stored `09:00`
 * against `PLATFORM_TIMEZONE`, so reading it on the tablet's own zone is how a
 * till in one place shows a schedule the marketplace is not acting on.
 */

export type ScheduleEdge =
  /** Inside a window now; `at` is when it ends. */
  | { state: 'open'; at: Dayjs }
  /** Outside every window; `at` is the next opening, if there is one this week. */
  | { state: 'closed'; at: Dayjs | null }
  /** No weekly schedule at all, which the server treats as "always within hours". */
  | { state: 'no-schedule'; at: null };

/**
 * Puts a stored wall clock onto a given day.
 *
 * The column is a Postgres `time`, so it arrives as `"09:00:00"` — parsed by
 * hand rather than through `dayjs(value, 'HH:mm')`, because that needs
 * `customParseFormat` loaded and silently returns Invalid Date without it.
 */
function at(day: Dayjs, clock: string): Dayjs {
  const [hour, minute] = clock.split(':');
  return day
    .hour(Number(hour) || 0)
    .minute(Number(minute) || 0)
    .second(0)
    .millisecond(0);
}

export function scheduleEdge(
  windows: OpeningWindow[] | undefined,
  now: Dayjs = nowInZone(),
): ScheduleEdge {
  if (!windows || windows.length === 0) return { state: 'no-schedule', at: null };

  /*
   * A window never crosses midnight — the server refuses `closesAt <= opensAt`
   * and asks for it to be split into two — so "inside" is a plain comparison
   * against today's rows and needs no wrap-around case.
   */
  for (const window of windows) {
    if (window.dayOfWeek !== now.day()) continue;
    const opens = at(now, window.opensAt);
    const closes = at(now, window.closesAt);
    if (!now.isBefore(opens) && now.isBefore(closes)) return { state: 'open', at: closes };
  }

  // Today's later windows first, then forward a week. Seven days is the whole
  // schedule, so failing to find one means the shop never opens.
  for (let ahead = 0; ahead <= 7; ahead += 1) {
    const day = now.add(ahead, 'day');
    const next = windows
      .filter((window) => window.dayOfWeek === day.day())
      .map((window) => at(day, window.opensAt))
      .filter((instant) => instant.isAfter(now))
      .sort((a, b) => a.valueOf() - b.valueOf())[0];
    if (next) return { state: 'closed', at: next };
  }

  return { state: 'closed', at: null };
}

/**
 * "at 9:00 am", "at 9:00 am tomorrow", "at 9:00 am on Monday".
 *
 * A bare time is ambiguous the moment it is not today — "opens at 9:00 am" on a
 * Sunday evening reads as twelve hours away when it may be a day and a half.
 */
export function whenLabel(instant: Dayjs, now: Dayjs = nowInZone()): string {
  const time = instant.format('h:mm a');
  const days = instant.startOf('day').diff(now.startOf('day'), 'day');
  if (days === 0) return `at ${time}`;
  if (days === 1) return `at ${time} tomorrow`;
  if (days < 7) return `at ${time} on ${instant.format('dddd')}`;
  return `at ${time} on ${instant.format('D MMM')}`;
}
